import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { videoLessonsApi, CreateVideoLessonDto, VideoLesson, VideoLessonType } from '../../../api/video-lessons';
import { UseVideoLessonsReturn } from '../types';

const initialFormData: CreateVideoLessonDto = {
  sort_id: 1,
  title: '',
  description: '',
  type: 'other',
  url: '',
};

const buildPayload = (formData: CreateVideoLessonDto): CreateVideoLessonDto => {
  const trimmedDescription = formData.description?.trim();
  return {
    ...formData,
    description: trimmedDescription ? trimmedDescription : null,
  };
};

export function useVideoLessons(): UseVideoLessonsReturn {
  const [videoLessons, setVideoLessons] = useState<VideoLesson[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingLesson, setEditingLesson] = useState<VideoLesson | null>(null);
  const [formData, setFormData] = useState<CreateVideoLessonDto>(initialFormData);

  const fetchVideoLessons = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await videoLessonsApi.getAll();
      setVideoLessons(data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch video lessons');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVideoLessons();
  }, [fetchVideoLessons]);

  const handleCreate = async () => {
    try {
      await videoLessonsApi.create(buildPayload(formData));
      toast.success('Video lesson created successfully');
      setIsCreateDialogOpen(false);
      setFormData(initialFormData);
      fetchVideoLessons();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create video lesson');
    }
  };

  const handleEdit = (lesson: VideoLesson) => {
    setEditingLesson(lesson);
    setFormData({
      sort_id: lesson.sort_id,
      title: lesson.title,
      description: lesson.description || '',
      type: lesson.type as VideoLessonType,
      url: lesson.url,
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingLesson) return;
    try {
      await videoLessonsApi.update(editingLesson.id, buildPayload(formData));
      toast.success('Video lesson updated successfully');
      setIsEditDialogOpen(false);
      setEditingLesson(null);
      fetchVideoLessons();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update video lesson');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this video lesson?')) return;
    try {
      await videoLessonsApi.delete(id);
      toast.success('Video lesson deleted successfully');
      fetchVideoLessons();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete video lesson');
    }
  };

  return {
    videoLessons,
    isLoading,
    isCreateDialogOpen,
    isEditDialogOpen,
    editingLesson,
    formData,
    setIsCreateDialogOpen,
    setIsEditDialogOpen,
    setFormData,
    handleCreate,
    handleEdit,
    handleUpdate,
    handleDelete,
  };
}
